import React, { useEffect, useRef, useCallback } from 'react';

/**
 * Cloudflare Turnstile widget (free, privacy-friendly CAPTCHA).
 *
 * Renders only when REACT_APP_TURNSTILE_SITE_KEY is set — otherwise it renders
 * nothing and immediately reports an empty token, so local/dev flows that don't
 * configure Turnstile keep working. The matching backend verification also no-ops
 * when TURNSTILE_SECRET_KEY is unset.
 *
 * Loads the Cloudflare script once on demand (no npm dependency).
 *
 * @param {(token: string) => void} onToken  called with the solved token (or '' when disabled)
 */
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function Turnstile({ onToken, theme = 'auto', className = '' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const handleToken = useCallback((t) => {
    if (typeof onToken === 'function') onToken(t);
  }, [onToken]);

  useEffect(() => {
    // No site key -> CAPTCHA disabled; report empty token so callers proceed.
    if (!siteKey) {
      handleToken('');
      return;
    }

    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => handleToken(token),
          'expired-callback': () => handleToken(''),
          'error-callback': () => handleToken('')
        });
      })
      .catch((err) => {
        console.warn('Turnstile failed to load:', err);
        handleToken('');
      });

    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (e) { /* ignore */ }
    };
  }, [siteKey, theme, handleToken]);

  if (!siteKey) return null;
  return <div ref={containerRef} className={className} />;
}

export default Turnstile;
